import { ArrowLeft, Users, Heart, Sparkles, Phone, MessageCircle, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function SupportNetworkGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-rose-700 hover:text-rose-800 hover:bg-rose-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-rose-100 rounded-full">
              <Users className="w-8 h-8 text-rose-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Support Network</h1>
              <p className="text-rose-600">Week 12, Day 6 - Keeping Your Glow</p>
            </div>
          </div>

          <div className="prose prose-rose max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Identify 3 people who support your growth. Reach out to each one today - thank them and 
              share what you're committed to going forward. Ask them to check in on you.
            </p>

            <div className="bg-rose-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Support Networks Matter
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Accountability increases follow-through by 65%</li>
                <li>• Support systems are crucial when motivation wanes</li>
                <li>• Sharing goals makes them feel more real</li>
                <li>• Others can see our blind spots and remind us of our commitments</li>
                <li>• Having people to celebrate with makes success sweeter</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Who Supports Your Growth?</h2>
            
            <p className="text-gray-700 mb-4">Think about people who:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">The Cheerleader</h3>
                <p className="text-gray-600 text-sm">Someone who celebrates your wins, believes in you, and offers encouragement.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">The Truth-Teller</h3>
                <p className="text-gray-600 text-sm">Someone who'll be honest with you when you're slipping or making excuses.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">The Fellow Traveler</h3>
                <p className="text-gray-600 text-sm">Someone on their own growth journey who understands the work involved.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">The Wise One</h3>
                <p className="text-gray-600 text-sm">Someone who offers perspective, has been through similar challenges, or mentors you.</p>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Your Reach-Out Template</h2>
            
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <p className="text-gray-700 italic mb-4">
                "Hey [Name], I wanted to reach out because you've been such an important part of my 
                growth lately. I just finished a 12-week wellness program and I'm committed to continuing 
                [specific habits/goals]. Would you be willing to check in on me occasionally and ask how 
                I'm doing with it? It would mean a lot to have your support. Thank you for being someone 
                I can count on."
              </p>
              <p className="text-rose-600 text-sm">Personalize this based on your relationship and what you're asking for.</p>
            </div>

            <div className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                What to Share
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Your top 3-5 non-negotiable habits going forward</li>
                <li>• One specific goal you're working toward</li>
                <li>• What kind of check-in would help (weekly text? monthly call?)</li>
                <li>• Permission to ask you the hard questions</li>
                <li>• What you'll do for them in return</li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Building Mutual Support
              </h2>
              <p className="text-gray-700 mb-4">
                The best support networks are reciprocal. Offer to:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Check in on their goals too</li>
                <li>• Be a sounding board when they need to talk</li>
                <li>• Celebrate their wins</li>
                <li>• Hold them accountable in return</li>
                <li>• Schedule regular connection time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
